import React, { FC, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { Button } from "@natera/form";
import { routes } from "@app/routing";
import {
  storeGisReturnUrl,
  storeGisEntrySource,
  GIS_ENTRY_SOURCE_PUBLIC,
} from "@app/utils/gisEntrySource";
import { clearGuestLookupPrefill } from "@app/pages/private/scheduleAGeneticInformationSession/context";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import PortalPhoneImage from "@assets/images/gis_entry_portal_phone.png";

import "./publicGisEntry.scss";

const messages = defineMessages({
  title: {
    id: "publicGisEntry.title",
    defaultMessage: "Schedule Your Genetic Information Session",
  },
  subtitle: {
    id: "publicGisEntry.subtitle",
    defaultMessage: "We'll connect you with Natera's genetic counselor.",
  },
  portalHeading: {
    id: "publicGisEntry.portalHeading",
    defaultMessage: "Natera's Patient Portal",
  },
  portalBody: {
    id: "publicGisEntry.portalBody",
    defaultMessage:
      "Quick, simple sign-up. Manage results, appointments, and bills in one place.",
  },
  logIn: {
    id: "publicGisEntry.logIn",
    defaultMessage: "Continue with Patient Portal",
  },
  continueAsGuest: {
    id: "publicGisEntry.continueAsGuest",
    defaultMessage: "Continue as Guest",
  },
});

const PublicGisEntry: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  // Track public-entry page view once on mount. Mirrors the
  // `upp_view_*` convention used for tokenized landing pages and
  // marks the start of the public GIS scheduling funnel.
  useEffect(() => {
    heap.track(HEAP_EVENTS.upp_view_gis_entry, {
      location: HeapEventLocation.gis_public_entry_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
    });
  }, []);

  const navigateWithReturnUrl = useCallback(
    (target: string) => {
      storeGisReturnUrl(routes.gisTriage);
      storeGisEntrySource(GIS_ENTRY_SOURCE_PUBLIC);
      navigate(`${target}?returnUrl=${encodeURIComponent(routes.gisTriage)}`);
    },
    [navigate]
  );

  const handleLogIn = useCallback(() => {
    heap.track(HEAP_EVENTS.upp_click_gis_entry_login, {
      location: HeapEventLocation.gis_public_entry_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
    });
    navigateWithReturnUrl(routes.logIn);
  }, [navigateWithReturnUrl]);

  const handleContinueAsGuest = useCallback(() => {
    heap.track(HEAP_EVENTS.upp_click_gis_entry_guest, {
      location: HeapEventLocation.gis_public_entry_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
    });
    clearGuestLookupPrefill();
    navigate(routes.publicGisOrderLookup);
  }, [navigate]);

  return (
    <div className="public-gis-entry">
      <div className="public-gis-entry__card">
        <div className="public-gis-entry__hero">
          <img
            className="public-gis-entry__hero-image"
            src={PortalPhoneImage}
            alt=""
            aria-hidden
          />
          <div className="public-gis-entry__hero-shade" aria-hidden />
          <div className="public-gis-entry__hero-message">
            <div className="public-gis-entry__hero-text">
              <h2 className="public-gis-entry__hero-heading">
                {intl.formatMessage(messages.portalHeading)}
              </h2>
              <p className="public-gis-entry__hero-body">
                {intl.formatMessage(messages.portalBody)}
              </p>
            </div>
          </div>
        </div>

        <div className="public-gis-entry__content">
          <div className="public-gis-entry__header">
            <h1 className="public-gis-entry__title">
              {intl.formatMessage(messages.title)}
            </h1>
            <p className="public-gis-entry__subtitle">
              {intl.formatMessage(messages.subtitle)}
            </p>
          </div>

          <div className="public-gis-entry__actions">
            <Button
              className="public-gis-entry__btn-primary"
              unelevated
              onClick={handleLogIn}
            >
              {intl.formatMessage(messages.logIn)}
            </Button>
            <button
              type="button"
              className="public-gis-entry__btn-guest"
              onClick={handleContinueAsGuest}
            >
              {intl.formatMessage(messages.continueAsGuest)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicGisEntry;
